import { useId, type FormEvent } from "react";
import {
  TRANSFER_BASES,
  type MoneyAccount,
  type TransferBasis,
  type TransferInput,
  type TransferStatus,
} from "@/api/cash";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type TransferFormProps = {
  accounts: MoneyAccount[];
  onSubmit: (input: TransferInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

const STATUSES: { value: TransferStatus; label: string }[] = [
  { value: "completed", label: "Completed - the money has moved" },
  { value: "pending", label: "Pending - instructed, not yet received" },
];

/** Money from one account to another. Between two accounts in the same currency the
 *  received amount is the amount less deductions; across currencies it is typed, never
 *  guessed. */
export function TransferForm({ accounts, onSubmit, onCancel, onReload }: TransferFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    made_on: todayIso(),
    from_account_id: accounts.find((a) => a.is_default_out)?.id ?? "",
    to_account_id: "",
    amount: "",
    deductions: "0.00",
    received_amount: "",
    fx_rate: "",
    basis: "own_account" as TransferBasis,
    reference: "",
    notes: "",
    status: "completed" as TransferStatus,
  });
  const { error, errors, saving, run } = useSubmitState();
  const from = accounts.find((a) => a.id === values.from_account_id);
  const to = accounts.find((a) => a.id === values.to_account_id);
  const crossCurrency = from !== undefined && to !== undefined && from.currency !== to.currency;
  const options = accounts.map((a) => ({ value: a.id, label: `${a.name} (${a.currency})` }));

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        made_on: values.made_on,
        from_account_id: values.from_account_id,
        to_account_id: values.to_account_id,
        amount: values.amount.trim(),
        deductions: values.deductions.trim() || "0.00",
        received_amount: crossCurrency ? values.received_amount.trim() || null : null,
        fx_rate: crossCurrency ? values.fx_rate.trim() || null : null,
        basis: values.basis,
        reference: values.reference.trim(),
        notes: values.notes.trim(),
        status: values.status,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-on`}
          label="Date"
          type="date"
          value={values.made_on}
          onChange={(v) => set("made_on", v)}
          error={errors.made_on}
        />
        <SelectField
          id={`${id}-from`}
          label="From"
          value={values.from_account_id}
          onChange={(v) => set("from_account_id", v)}
          placeholder="Choose an account"
          options={options}
          error={errors.from_account_id}
        />
        <SelectField
          id={`${id}-to`}
          label="To"
          value={values.to_account_id}
          onChange={(v) => set("to_account_id", v)}
          placeholder="Choose an account"
          options={options}
          error={errors.to_account_id}
        />
        <TextField
          id={`${id}-amount`}
          label={`Amount${from ? ` (${from.currency})` : ""}`}
          value={values.amount}
          onChange={(v) => set("amount", v)}
          error={errors.amount}
        />
        <TextField
          id={`${id}-deductions`}
          label="Deductions"
          value={values.deductions}
          onChange={(v) => set("deductions", v)}
          error={errors.deductions}
          hint="TDS, bank charges - taken from the amount on the way"
        />
        {crossCurrency && (
          <>
            <TextField
              id={`${id}-received`}
              label={`Received (${to.currency})`}
              value={values.received_amount}
              onChange={(v) => set("received_amount", v)}
              error={errors.received_amount}
              hint={`${from.currency} is not converted at a guessed rate: type what ${to.name} received`}
            />
            <TextField
              id={`${id}-rate`}
              label="Rate"
              value={values.fx_rate}
              onChange={(v) => set("fx_rate", v)}
              error={errors.fx_rate}
              hint={`${to.currency} per ${from.currency}, if known`}
            />
          </>
        )}
        <SelectField
          id={`${id}-basis`}
          label="Why"
          value={values.basis}
          onChange={(v) => set("basis", v as TransferBasis)}
          options={TRANSFER_BASES}
          error={errors.basis}
        />
        <SelectField
          id={`${id}-status`}
          label="Status"
          value={values.status}
          onChange={(v) => set("status", v as TransferStatus)}
          options={STATUSES}
          error={errors.status}
        />
        <TextField
          id={`${id}-reference`}
          label="Reference"
          value={values.reference}
          onChange={(v) => set("reference", v)}
          error={errors.reference}
        />
      </div>
      <TextAreaField
        id={`${id}-notes`}
        label="Notes"
        value={values.notes}
        onChange={(v) => set("notes", v)}
        error={errors.notes}
      />
      <FormActions
        saving={saving}
        submitLabel="Record transfer"
        onCancel={onCancel}
        disabled={!from || !to}
      />
    </form>
  );
}
